import React, { useEffect, useRef, useState } from "react";
import {
  collection, getDocs, getCountFromServer,
  orderBy, query, limit, startAfter, where,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { FiChevronLeft, FiChevronRight, FiExternalLink, FiCode } from "react-icons/fi";
import DocViewerModal from "../components/DocViewerModal";
import logo from "../assets/logo.png";

const PAGE_SIZE = 20;

const SORT_OPTIONS = [
  { value: "name_asc",     label: "Name A → Z",      field: "name",      dir: "asc"  },
  { value: "name_desc",    label: "Name Z → A",      field: "name",      dir: "desc" },
  { value: "cups_desc",    label: "Most Cups",       field: "cups",      dir: "desc" },
  { value: "created_desc", label: "Newest accounts", field: "createdAt", dir: "desc" },
  { value: "created_asc",  label: "Oldest accounts", field: "createdAt", dir: "asc"  },
];

// ── Avatar ──────────────────────────────────────────────────────
function Avatar({ name, imageUrl }) {
  const [imgFailed, setImgFailed] = useState(false);
  const initials = (name || "?")
    .split(" ").map((w) => w[0] || "").join("").slice(0, 2).toUpperCase();

  if (imageUrl && !imgFailed) {
    return (
      <img
        src={imageUrl}
        alt={name}
        onError={() => setImgFailed(true)}
        style={{
          width: 38, height: 38, borderRadius: "50%",
          objectFit: "cover", flexShrink: 0,
          border: "2px solid #E0E0E0",
        }}
      />
    );
  }

  return (
    <div style={{
      width: 38, height: 38, borderRadius: "50%",
      background: "#2a9d8f", color: "#fff", flexShrink: 0,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontWeight: 700, fontSize: 13, fontFamily: "'Quicksand', sans-serif",
    }}>
      {initials}
    </div>
  );
}

// ── Users ────────────────────────────────────────────────────────
export default function Users() {
  const navigate = useNavigate();

  // Cursor cache: { "sort::search": [null, docSnap, docSnap, ...] }
  const cursorsRef = useRef({});
  const didMountRef = useRef(false);

  const [queryState, setQueryState] = useState({ sort: "name_asc", search: "", page: 0 });
  const [inputSearch, setInputSearch] = useState("");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(null);
  const [viewDoc, setViewDoc] = useState(null);

  // Fetch total count once on mount
  useEffect(() => {
    getCountFromServer(collection(db, "users"))
      .then((snap) => setTotalCount(snap.data().count))
      .catch(() => setTotalCount(null));
  }, []);

  // Debounce search — skip on mount so the initial empty value doesn't
  // wipe the cursor cache 350ms after first load.
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    const t = setTimeout(() => {
      const trimmed = inputSearch.trim();
      setQueryState((q) => {
        if (q.search === trimmed) return q; // no change — don't re-render
        cursorsRef.current = {};            // clear cache only when search changes
        return { ...q, search: trimmed, page: 0 };
      });
    }, 350);
    return () => clearTimeout(t);
  }, [inputSearch]);

  // Fetch whenever queryState changes
  useEffect(() => {
    let cancelled = false;

    const fetchUsers = async () => {
      setLoading(true);

      const { sort, search, page } = queryState;
      const sortOpt = SORT_OPTIONS.find((o) => o.value === sort) || SORT_OPTIONS[0];
      const cursorKey = `${sort}::${search}`;

      if (!cursorsRef.current[cursorKey]) {
        cursorsRef.current[cursorKey] = [null];
      }
      const cursor = cursorsRef.current[cursorKey][page] ?? null;

      try {
        let constraints;

        if (search.length >= 2) {
          // Prefix search on name (must orderBy name for range query)
          constraints = [
            orderBy("name"),
            where("name", ">=", search),
            where("name", "<=", search + "\uf8ff"),
            ...(cursor ? [startAfter(cursor)] : []),
            limit(PAGE_SIZE + 1),
          ];
        } else {
          constraints = [
            orderBy(sortOpt.field, sortOpt.dir),
            ...(cursor ? [startAfter(cursor)] : []),
            limit(PAGE_SIZE + 1),
          ];
        }

        const snap = await getDocs(query(collection(db, "users"), ...constraints));

        if (cancelled) return;

        const docs = snap.docs;
        const more = docs.length > PAGE_SIZE;
        const pageUsers = docs.slice(0, PAGE_SIZE).map((d) => ({ id: d.id, ...d.data() }));

        // Cache cursor for next page
        if (more) {
          cursorsRef.current[cursorKey][page + 1] = docs[PAGE_SIZE - 1];
        }

        setUsers(pageUsers);
        setHasMore(more);
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to fetch users:", err);
          setUsers([]);
          setHasMore(false);
        }
      }

      if (!cancelled) setLoading(false);
    };

    fetchUsers();
    return () => { cancelled = true; };
  }, [queryState]);

  const changeSort = (newSort) => {
    cursorsRef.current = {};
    setQueryState({ sort: newSort, search: queryState.search, page: 0 });
  };

  const goNext = () => setQueryState((q) => ({ ...q, page: q.page + 1 }));
  const goPrev = () => setQueryState((q) => ({ ...q, page: Math.max(0, q.page - 1) }));

  const isSearchMode = queryState.search.length >= 2;
  const pageStart = queryState.page * PAGE_SIZE + 1;
  const pageEnd = queryState.page * PAGE_SIZE + users.length;

  const selectStyle = {
    fontFamily: "'Quicksand', sans-serif", fontSize: 14, fontWeight: 600,
    padding: "9px 12px", borderRadius: 10, border: "1.5px solid #E0E0E0",
    background: "#fff", color: "#000", cursor: "pointer", outline: "none",
    flex: "0 1 170px", minWidth: 140,
  };

  const iconBtnStyle = {
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    width: 34, height: 34, borderRadius: 10,
    border: "1.5px solid #2a9d8f", borderBottomWidth: 3,
    background: "#fff", color: "#2a9d8f", cursor: "pointer",
    transition: "transform 80ms ease, border-bottom-width 80ms ease",
    flexShrink: 0,
  };

  return (
    <div style={{ fontFamily: "'Quicksand', sans-serif", maxWidth: 1200 }}>

      {/* ── Header ── */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0 }}>Users</h1>
        <div style={{ fontSize: 14, fontWeight: 600, color: "#606060", marginTop: 4 }}>
          {totalCount !== null
            ? `${totalCount.toLocaleString()} total users`
            : "Counting users…"}
        </div>
      </div>

      {/* ── Filter Bar ── */}
      <div style={{
        background: "#fff", borderRadius: 16, border: "1px solid #E0E0E0",
        padding: "14px 18px", marginBottom: 20,
        display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center",
      }}>
        {/* Search input */}
        <div style={{ position: "relative", flex: "1 1 240px", minWidth: 180 }}>
          <span style={{
            position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)",
            color: "#aaa", pointerEvents: "none", fontSize: 15,
          }}>
            🔍
          </span>
          <input
            type="text"
            placeholder="Search by name…"
            value={inputSearch}
            onChange={(e) => setInputSearch(e.target.value)}
            style={{
              fontFamily: "'Quicksand', sans-serif", fontSize: 14, fontWeight: 500,
              padding: "9px 12px 9px 36px", borderRadius: 10,
              border: "1.5px solid #E0E0E0", background: "#fff", color: "#000",
              outline: "none", width: "100%",
            }}
          />
        </div>

        {/* Sort — hidden during search (range queries must order by name) */}
        {!isSearchMode && (
          <select value={queryState.sort} onChange={(e) => changeSort(e.target.value)} style={selectStyle}>
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        )}

        {/* Result summary */}
        <div style={{ marginLeft: "auto", fontSize: 13, fontWeight: 600, color: "#606060", whiteSpace: "nowrap" }}>
          {loading
            ? "Loading…"
            : isSearchMode
            ? `${users.length} result${users.length !== 1 ? "s" : ""} for "${queryState.search}"`
            : `Showing ${pageStart}–${pageEnd}${totalCount ? ` of ${totalCount.toLocaleString()}` : ""}`}
        </div>
      </div>

      {!isSearchMode && queryState.sort.startsWith("created_") && (
        <div style={{ fontSize: 12, fontWeight: 600, color: "#909090", margin: "-8px 2px 16px" }}>
          Sorted by account creation date. Users without a recorded signup date are not shown in this order.
        </div>
      )}

      {/* ── Table ── */}
      <div style={{
        borderRadius: 16, overflow: "auto",
        border: "1.5px solid #E0E0E0", boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        marginBottom: 16,
      }}>
        <table style={{
          width: "100%", minWidth: 640, borderCollapse: "collapse",
          fontFamily: "'Quicksand', sans-serif", fontSize: 14, background: "#fff",
        }}>
          <thead>
            <tr style={{ background: "#2a9d8f" }}>
              {[
                { label: "User",           width: undefined },
                { label: "Email",          width: undefined },
                { label: "Status",         width: 110 },
                { label: "Cups",           width: 90  },
                { label: "Lessons Done",   width: 130 },
                { label: "",               width: 96  },
              ].map(({ label, width }, i) => (
                <th
                  key={i}
                  style={{
                    padding: "12px 16px", width,
                    color: "#fff", fontWeight: 700, fontSize: 12,
                    textTransform: "uppercase", letterSpacing: "0.06em",
                    textAlign: i === 5 ? "right" : "left",
                  }}
                >
                  {label}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} style={{ padding: 52, textAlign: "center" }}>
                  <div style={{ display: "inline-flex", alignItems: "center", gap: 10, color: "#606060", fontWeight: 600 }}>
                    <div style={{
                      width: 18, height: 18, borderRadius: "50%",
                      border: "2.5px solid #E0E0E0", borderTop: "2.5px solid #2a9d8f",
                      animation: "spin 0.8s linear infinite",
                    }} />
                    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                    Loading users…
                  </div>
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ padding: 52, textAlign: "center", color: "#606060", fontWeight: 600 }}>
                  {isSearchMode
                    ? `No users match "${queryState.search}".`
                    : "No users found."}
                </td>
              </tr>
            ) : (
              users.map((user, idx) => {
                const completedCount = Array.isArray(user.completedLessons)
                  ? user.completedLessons.length : 0;
                const isLast = idx === users.length - 1;

                return (
                  <tr
                    key={user.id}
                    onClick={() => navigate(`/users/${user.id}`)}
                    style={{
                      borderBottom: isLast ? "none" : "1px solid #f3f3f3",
                      cursor: "pointer",
                      transition: "background 0.1s ease",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(42,157,143,0.04)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; }}
                  >
                    {/* User: avatar + name + @username */}
                    <td style={{ padding: "12px 16px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <Avatar name={user.name} imageUrl={user.imageurl} />
                        <div style={{ minWidth: 0 }}>
                          <div style={{
                            fontWeight: 700, fontSize: 14,
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                          }}>
                            {user.name || "Unknown"}
                          </div>
                          {user.username && (
                            <div style={{ fontSize: 12, color: "#606060", fontWeight: 500 }}>
                              @{user.username}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Email */}
                    <td style={{ padding: "12px 16px" }}>
                      <div style={{
                        fontSize: 13, color: "#606060",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        maxWidth: 220,
                      }}>
                        {user.email || "—"}
                      </div>
                    </td>

                    {/* Verified badge */}
                    <td style={{ padding: "12px 16px", width: 110 }}>
                      <span style={{
                        display: "inline-flex", alignItems: "center", gap: 4,
                        borderRadius: 999, padding: "3px 10px",
                        fontSize: 11, fontWeight: 700,
                        background: user.verified ? "#50C87818" : "#f0f0f0",
                        color: user.verified ? "#27774a" : "#888",
                        border: `1px solid ${user.verified ? "#50C878" : "#E0E0E0"}`,
                      }}>
                        {user.verified ? "✓ Verified" : "Unverified"}
                      </span>
                    </td>

                    {/* Cups */}
                    <td style={{ padding: "12px 16px", width: 90 }}>
                      <span style={{ fontWeight: 700, color: "#2a9d8f", fontSize: 14 }}>
                        {(user.cups ?? 0).toLocaleString()}
                      </span>
                      <img src={logo} alt="" style={{ width: 15, height: 15, borderRadius: 4, marginLeft: 4, verticalAlign: "middle" }} />
                    </td>

                    {/* Completed lessons */}
                    <td style={{ padding: "12px 16px", width: 130 }}>
                      <span style={{
                        fontWeight: 700, fontSize: 14,
                        color: completedCount > 0 ? "#2a9d8f" : "#bbb",
                      }}>
                        {completedCount}
                      </span>
                      <span style={{ fontSize: 12, color: "#aaa", marginLeft: 4 }}>
                        lesson{completedCount !== 1 ? "s" : ""}
                      </span>
                    </td>

                    {/* View JSON + Open buttons */}
                    <td style={{ padding: "12px 16px", width: 96, textAlign: "right" }}>
                      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                        <button
                          onClick={(e) => { e.stopPropagation(); setViewDoc({ path: `users/${user.id}`, data: user }); }}
                          title="View Firestore document"
                          style={{ ...iconBtnStyle, border: "1.5px solid #3a82c6", color: "#3a82c6" }}
                          onMouseDown={(e) => {
                            e.currentTarget.style.transform = "translateY(2px)";
                            e.currentTarget.style.borderBottomWidth = "1.5px";
                          }}
                          onMouseUp={(e) => {
                            e.currentTarget.style.transform = "";
                            e.currentTarget.style.borderBottomWidth = "3px";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "";
                            e.currentTarget.style.borderBottomWidth = "3px";
                          }}
                        >
                          <FiCode size={15} />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate(`/users/${user.id}`); }}
                          title="Open user"
                          style={iconBtnStyle}
                          onMouseDown={(e) => {
                            e.currentTarget.style.transform = "translateY(2px)";
                            e.currentTarget.style.borderBottomWidth = "1.5px";
                          }}
                          onMouseUp={(e) => {
                            e.currentTarget.style.transform = "";
                            e.currentTarget.style.borderBottomWidth = "3px";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "";
                            e.currentTarget.style.borderBottomWidth = "3px";
                          }}
                        >
                          <FiExternalLink size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* ── Pagination ── */}
      {!isSearchMode && !loading && (
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <button
            onClick={goPrev}
            disabled={queryState.page === 0}
            className="primary-button"
            style={{
              backgroundColor: "#fff", color: queryState.page === 0 ? "#bbb" : "#000",
              borderColor: "#E0E0E0", fontSize: 14, padding: "9px 20px",
              display: "inline-flex", alignItems: "center", gap: 6,
            }}
          >
            <FiChevronLeft size={16} /> Previous
          </button>

          <div style={{ fontSize: 14, fontWeight: 700, color: "#606060" }}>
            Page {queryState.page + 1}
            {totalCount && (
              <span style={{ fontWeight: 500, marginLeft: 4 }}>
                of {Math.ceil(totalCount / PAGE_SIZE)}
              </span>
            )}
          </div>

          <button
            onClick={goNext}
            disabled={!hasMore}
            className="primary-button"
            style={{
              backgroundColor: hasMore ? "#2a9d8f" : "#fff",
              color: hasMore ? "#fff" : "#bbb",
              borderColor: hasMore ? "#258D80" : "#E0E0E0",
              fontSize: 14, padding: "9px 20px",
              display: "inline-flex", alignItems: "center", gap: 6,
            }}
          >
            Next <FiChevronRight size={16} />
          </button>
        </div>
      )}

      <DocViewerModal
        open={!!viewDoc}
        onClose={() => setViewDoc(null)}
        path={viewDoc?.path}
        data={viewDoc?.data}
      />
    </div>
  );
}
